import React from 'react';
import { COLORS } from '../data/mockData';
import { CONTACT_EMAIL } from '../data/contact';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import { FileText, Shield, Scale } from 'lucide-react';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    content: 'By accessing or using ThriveLonger.ai ("the Service"), you agree to be bound by these Terms of Use. If you do not agree with any part of these terms, you may not use the Service. We reserve the right to modify these terms at any time, and continued use constitutes acceptance of modifications.',
  },
  {
    title: '2. Description of Service',
    content: 'ThriveLonger.ai provides a health optimization platform that includes personalized health scoring, AI-powered coaching, research libraries, goal tracking, wearable device integration, and community features. The Service is designed for informational and educational purposes and is NOT a substitute for professional medical advice, diagnosis, or treatment.',
  },
  {
    title: '3. Medical Disclaimer',
    content: 'The health scores, predictions, AI coaching recommendations, and research content provided by ThriveLonger.ai are for informational purposes only. Always seek the advice of your physician or other qualified health provider with any questions regarding a medical condition. Never disregard professional medical advice or delay seeking it because of something you have read or received through our Service.',
  },
  {
    title: '4. User Accounts & Registration',
    content: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your login credentials and are fully responsible for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account.',
  },
  {
    title: '5. Subscription & Billing',
    content: 'ThriveLonger.ai offers free and paid subscription tiers (Explorer, Analyst, Optimizer, Elite). Paid subscriptions are billed monthly. You may cancel your subscription at any time, and your access will continue through the end of the current billing period. No refunds are provided for partial months.',
  },
  {
    title: '6. Data Privacy & Security',
    content: 'We take your health data seriously. ThriveLonger.ai is HIPAA-compliant and employs AES-256 encryption at rest and TLS 1.3 in transit. Your health data is never sold to third parties. For more details, please review our Privacy Policy.',
  },
  {
    title: '7. Intellectual Property',
    content: 'All content, features, and functionality of ThriveLonger.ai, including but not limited to the health scoring algorithms, AI models, research content, and user interface design, are the exclusive property of ThriveLonger.ai and are protected by copyright, trademark, and other intellectual property laws.',
  },
  {
    title: '8. Limitation of Liability',
    content: 'ThriveLonger.ai shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service. Our total liability for any claims arising from these Terms shall not exceed the amount paid by you for the Service during the 12 months preceding the claim.',
  },
  {
    title: '9. Termination',
    content: 'We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason at our sole discretion.',
  },
  {
    title: '10. Contact Information',
    content: `If you have any questions about these Terms, please contact us at ${CONTACT_EMAIL} or write to ThriveLonger.ai, 123 Health Way, San Francisco, CA 94105.`,
  },
];

export default function TermsPage() {
  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh' }}>
      <SiteHeader />

      {/* Header */}
      <section style={{ background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 50%, #F9F6F0 100%)', padding: '64px 20px 48px', textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 12 }}>
            <Scale size={28} color={COLORS.primary} />
          </div>
          <h1 data-testid="terms-title" style={{ fontSize: 'clamp(32px, 5vw, 44px)', fontWeight: 900, color: COLORS.textPrimary, lineHeight: 1.15, fontFamily: 'Outfit, sans-serif' }}>
            Terms of Use
          </h1>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, marginTop: 12 }}>Last updated: February 1, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: '40px 20px 56px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: '32px 28px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            {SECTIONS.map((s, i) => (
              <div key={i} style={{ marginBottom: i < SECTIONS.length - 1 ? 28 : 0, paddingBottom: i < SECTIONS.length - 1 ? 28 : 0, borderBottom: i < SECTIONS.length - 1 ? `1px solid ${COLORS.border}40` : 'none' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', marginBottom: 10 }}>{s.title}</h2>
                <p style={{ fontSize: 15, color: COLORS.textSecondary, lineHeight: 1.7 }}>{s.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
